import { Button } from "@mui/material";
import clsx from "clsx";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";

import { store, useLogStack, type LogEntry, type LogType } from "./logStack";

type ConsoleProps = {
  isOpen: boolean;
};

export function Console({ isOpen }: ConsoleProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: "0%" }}
          exit={{ y: "100%" }}
          transition={{ type: "tween", ease: "easeInOut", duration: 0.25 }}
          className="fixed bottom-0 left-0 z-10000 max-h-[60dvh] w-full"
        >
          <BaseConsole />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function BaseConsole() {
  const stack = useLogStack();

  return (
    <div className="h-[50dvh] overflow-auto bg-slate-900 p-2 pb-10">
      <Button onClick={() => store.clear()} size="small" color="info">
        Clear
      </Button>
      {[...stack].reverse().map((entry, index) => {
        return <ConsoleRow key={index} {...entry} />;
      })}
    </div>
  );
}

const titleVariants = {
  log: "text-slate-500",
  error: "text-red-700",
  warn: "text-yellow-700",
  info: "text-sky-700",
} satisfies Record<LogType, string>;

function ConsoleRow({ type, args, trace, timestamp }: LogEntry) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <button
      type="button"
      className="block w-full cursor-pointer border-b border-slate-800 py-2 text-left"
      onClick={() => setIsOpen((old) => !old)}
    >
      <div className={clsx("text-xs font-bold", titleVariants[type])}>
        {new Date(timestamp).toISOString()} {type}
      </div>
      <div className="font-mono text-sm text-slate-200">
        &gt; {args.map((arg) => JSON.stringify(arg)).join(" ")}
      </div>
      {isOpen ? (
        <pre className="font-mono text-xs text-slate-500">{trace}</pre>
      ) : null}
    </button>
  );
}
